import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { dossierConfigAPI } from '../services/api';

/**
 * Composant d'affichage dynamique de la fiche dossier
 * Utilise la configuration stockée en base pour afficher les sections et champs
 */
const DynamicDossierView = ({ 
  dossier, 
  editing = false, 
  editData = {}, 
  setEditData = () => {},
  statuts = [],
  gestionnaires = []
}) => {
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState({});

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await dossierConfigAPI.getFullConfig();
      setConfig(res.data);
    } catch (error) {
      console.error('Erreur chargement config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionCode) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionCode]: !prev[sectionCode]
    }));
  };

  // Récupérer la valeur d'un champ depuis le dossier (supporte les chemins comme "identite.nom")
  const getFieldValue = (fieldCode) => {
    // Champs calculés
    if (fieldCode === '_total_ressources') {
      const r = dossier.ressources || {};
      return (r.salaires || 0) + (r.indemnites_chomage || 0) + (r.pension_invalidite_ss || 0) +
             (r.retraite_base || 0) + (r.retraite_complementaire || 0) + 
             (r.allocation_logement || 0) + (r.rsa_prime_activite || 0) + 
             (r.aah_aeeh_pch || 0) + (r.autres_revenus || 0);
    }
    if (fieldCode === '_total_charges') {
      const c = dossier.charges || {};
      return (c.loyer || 0) + (c.energie || 0) + (c.eau || 0) +
             (c.assurance_habitation || 0) + (c.assurance_vehicule || 0) + 
             (c.cotisations_mutuelle || 0) + (c.telephonie || 0) + 
             (c.impot_revenu || 0) + (c.autres_charges || 0);
    }
    if (fieldCode === '_reste_a_vivre') {
      const ressources = getFieldValue('_total_ressources');
      const charges = getFieldValue('_total_charges');
      return ressources - charges;
    }

    // Champs normaux
    const parts = fieldCode.split('.');
    let value = dossier;
    for (const part of parts) {
      if (value === null || value === undefined) return null;
      value = value[part];
    }
    return value;
  };

  // Mettre à jour la valeur en mode édition
  const updateFieldValue = (fieldCode, newValue) => {
    const parts = fieldCode.split('.');
    if (parts.length === 1) {
      setEditData({ ...editData, [fieldCode]: newValue });
    } else {
      // Pour les champs imbriqués comme "identite.nom"
      const [parent, child] = parts;
      setEditData({
        ...editData,
        [parent]: {
          ...(editData[parent] || dossier[parent] || {}),
          [child]: newValue
        }
      });
    }
  };

  // Formater une valeur pour l'affichage
  const formatValue = (value, field) => {
    if (value === null || value === undefined || value === '') return '—';
    
    switch (field.format_affichage || field.type_champ) {
      case 'currency':
        return `${Number(value).toFixed(2)} ${field.suffixe || '€'}`;
      case 'date':
        if (typeof value === 'string' && value.includes('T')) {
          return new Date(value).toLocaleDateString('fr-FR');
        }
        return value;
      case 'phone':
        return value;
      default:
        return field.suffixe ? `${value} ${field.suffixe}` : value;
    }
  };

  // Rendu d'un champ en mode lecture
  const renderFieldDisplay = (field) => {
    const value = getFieldValue(field.code);
    
    // Cas spéciaux
    if (field.code === 'statut') {
      const statutInfo = statuts.find(s => s.code === value) || {};
      return (
        <span 
          className="inline-block px-3 py-1 rounded-full text-sm font-semibold"
          style={{ 
            backgroundColor: statutInfo.couleur ? `${statutInfo.couleur}20` : '#e5e7eb',
            color: statutInfo.couleur || '#374151'
          }}
        >
          {statutInfo.icone} {statutInfo.label || value}
        </span>
      );
    }
    
    if (field.code === 'priorite') {
      const icons = { high: '🔴', med: '🟠', low: '🟢' };
      return <span className="text-2xl">{icons[value] || '🟢'}</span>;
    }
    
    if (field.code === 'gestionnaire_id') {
      const gestionnaire = gestionnaires.find(g => g.id === value);
      return gestionnaire ? `${gestionnaire.prenom} ${gestionnaire.nom}` : '— Non assigné —';
    }

    // Valeurs calculées avec style spécial
    if (field.code === '_reste_a_vivre') {
      const val = getFieldValue(field.code);
      return (
        <span className={`text-xl font-bold ${val >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {formatValue(val, field)}
        </span>
      );
    }
    if (field.code === '_total_ressources') {
      return <span className="font-bold text-green-700">{formatValue(value, field)}</span>;
    }
    if (field.code === '_total_charges') {
      return <span className="font-bold text-red-700">{formatValue(value, field)}</span>;
    }

    return formatValue(value, field);
  };

  // Rendu d'un champ en mode édition
  const renderFieldEdit = (field) => {
    const value = getFieldValue(field.code);
    const editValue = getEditValue(field.code) ?? value;

    // Champs non éditables
    if (!field.editable) {
      return renderFieldDisplay(field);
    }

    // Select pour statut
    if (field.code === 'statut') {
      return (
        <select
          value={editValue || ''}
          onChange={(e) => updateFieldValue(field.code, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          {statuts.map(s => (
            <option key={s.code} value={s.code}>{s.icone} {s.label}</option>
          ))}
        </select>
      );
    }

    // Select pour priorité
    if (field.code === 'priorite') {
      return (
        <select
          value={editValue || 'low'}
          onChange={(e) => updateFieldValue(field.code, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="low">🟢 Basse</option>
          <option value="med">🟠 Moyenne</option>
          <option value="high">🔴 Haute</option>
        </select>
      );
    }

    // Select pour gestionnaire
    if (field.code === 'gestionnaire_id') {
      return (
        <select
          value={editValue || ''}
          onChange={(e) => updateFieldValue(field.code, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="">— Non assigné —</option>
          {gestionnaires.map(g => (
            <option key={g.id} value={g.id}>{g.prenom} {g.nom}</option>
          ))}
        </select>
      );
    }

    // Select générique
    if (field.type_champ === 'select' && field.options) {
      return (
        <select
          value={editValue || ''}
          onChange={(e) => updateFieldValue(field.code, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="">— Sélectionner —</option>
          {field.options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    // Textarea
    if (field.type_champ === 'textarea') {
      return (
        <textarea
          value={editValue || ''}
          onChange={(e) => updateFieldValue(field.code, e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder={field.placeholder}
        />
      );
    }

    // Input number/currency
    if (field.type_champ === 'currency' || field.type_champ === 'number') {
      return (
        <div className="relative">
          <input
            type="number"
            step="0.01"
            value={editValue || 0}
            onChange={(e) => updateFieldValue(field.code, parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            placeholder={field.placeholder}
          />
          {field.suffixe && (
            <span className="absolute right-3 top-2 text-gray-500">{field.suffixe}</span>
          )}
        </div>
      );
    }

    // Input text par défaut
    return (
      <input
        type={field.type_champ === 'email' ? 'email' : field.type_champ === 'phone' ? 'tel' : 'text'}
        value={editValue || ''}
        onChange={(e) => updateFieldValue(field.code, e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        placeholder={field.placeholder}
      />
    );
  };

  // Récupérer la valeur en cours d'édition
  const getEditValue = (fieldCode) => {
    const parts = fieldCode.split('.');
    if (parts.length === 1) {
      return editData[fieldCode];
    }
    const [parent, child] = parts;
    return editData[parent]?.[child];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        <span className="ml-3 text-gray-600">Chargement de la configuration...</span>
      </div>
    );
  }

  if (config.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>Configuration non initialisée.</p>
        <p className="text-sm mt-2">Allez dans Super Admin {'>'} Widgets {'>'} Configuration Fiche Dossier</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {config.map((section) => (
        <div 
          key={section.id}
          className={`rounded-xl border ${section.couleur_border} ${section.couleur_bg} overflow-hidden`}
        >
          {/* Header de la section */}
          <div 
            className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-black/5 transition"
            onClick={() => toggleSection(section.code)}
          >
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <span className="text-xl">{section.icone}</span>
              {section.nom}
            </h2>
            {collapsedSections[section.code] ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            )}
          </div>

          {/* Contenu de la section */}
          {!collapsedSections[section.code] && (
            <div className="px-5 pb-5">
              <div className="grid grid-cols-2 gap-4">
                {section.fields.map((field) => (
                  <div 
                    key={field.id} 
                    className={field.largeur === 'col-span-2' ? 'col-span-2' : ''}
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.obligatoire && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {editing ? renderFieldEdit(field) : (
                      <p className="text-gray-900">{renderFieldDisplay(field)}</p>
                    )}
                    {field.aide && (
                      <p className="text-xs text-gray-500 mt-1">{field.aide}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DynamicDossierView;
